import { Dispatch, useRef, useState } from 'react'
import Papa from 'papaparse'
import { CheckCircle2, CloudUpload, Download, FileText, X } from 'lucide-react'
import { cn } from 'components/UI/cn'
import { ImportData } from 'features/importerv2/importer-types'
import { WizardState, WizardAction } from '../wizardState'
import {
  downloadContactImportTemplate,
  MAX_IMPORT_ROWS,
} from '../contactImportFields'

interface UploadStepProps {
  state: WizardState
  dispatch: Dispatch<WizardAction>
}

/** Format a byte count as a short, human-readable label (e.g. "12 KB", "1.4 MB"). */
function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${bytes} B`
}

/**
 * Step 1 of the contact import wizard.
 *
 * Renders a CSV drag-and-drop zone with a "Browse File" button and a
 * "Download CSV template" link. On file selection the CSV is parsed
 * client-side with PapaParse:
 *  - fewer than 2 columns → inline error, no confirmation card
 *  - more than MAX_IMPORT_ROWS rows → inline cap error, no confirmation card
 *  - otherwise the parsed file + rows are stored in wizard state and a
 *    confirmation card is shown.
 *
 * Selecting a file does NOT auto-advance. The user must click
 * "Continue to Column Mapping", which is disabled until a valid file is loaded
 * and dispatches SET_STEP to the map step.
 */
export function UploadStep({ state, dispatch }: UploadStepProps) {
  const [parseError, setParseError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // A valid file is loaded when both the file and its parsed data are in state.
  const uploadedFile = state.file
  const importData = state.importData
  const hasValidFile = uploadedFile != null && importData != null

  /** Parse a CSV File and, on success, store the file + rows in wizard state. */
  function parseFile(file: File) {
    setParseError(null)

    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      delimitersToGuess: [';', ','],
      complete: (results) => {
        const fields = results.meta.fields ?? []

        if (fields.length < 2) {
          setParseError(
            'The file could not be parsed as a valid CSV. Ensure the file uses comma or semicolon delimiters and has at least 2 columns.'
          )
          return
        }

        if (results.data.length === 0) {
          setParseError(
            'This CSV has no data rows. Add at least one contact row below the header and try again.'
          )
          return
        }

        if (results.data.length > MAX_IMPORT_ROWS) {
          setParseError(
            `This file has ${results.data.length.toLocaleString()} rows. The importer currently supports up to ${MAX_IMPORT_ROWS.toLocaleString()} rows — please split it into smaller files.`
          )
          return
        }

        const data: ImportData = {
          rows: results.data as ImportData['rows'],
          headers: fields.map((h) => ({ original: h, mapped: 'ignore' })),
        }

        dispatch({ type: 'SET_FILE', file })
        dispatch({ type: 'SET_IMPORT_DATA', data })
      },
      error: () => {
        setParseError(
          'Failed to parse the CSV file. Please check the file format.'
        )
      },
    })
  }

  function handleFile(file: File | null | undefined) {
    if (!file) {
      return
    }
    parseFile(file)
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setIsDragging(false)
    handleFile(e.dataTransfer.files[0])
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    setIsDragging(true)
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    handleFile(e.target.files?.[0])
    e.target.value = ''
  }

  function handleRemove() {
    setParseError(null)
    dispatch({ type: 'RESET' })
  }

  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0 px-8 py-6 flex flex-col gap-6">
        {/* Drop zone */}
        <div
          role="button"
          tabIndex={0}
          className={cn(
            'relative rounded-xl border-2 border-dashed p-10 flex flex-col items-center gap-4 cursor-pointer transition-all duration-200',
            isDragging
              ? 'border-brand-bright bg-success-bg'
              : 'border-line bg-surface-sunken hover:border-brand-bright/50 hover:bg-success-bg/40'
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDragging(false)}
          onClick={() => !uploadedFile && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (!uploadedFile && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault()
              inputRef.current?.click()
            }
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleInputChange}
          />

          <div
            className={cn(
              'size-14 rounded-full flex items-center justify-center transition-colors duration-200',
              isDragging ? 'bg-success-bg' : 'bg-surface'
            )}
          >
            <CloudUpload
              className={cn(
                'size-7 transition-colors duration-200',
                isDragging ? 'text-brand-bright' : 'text-fg-muted'
              )}
            />
          </div>

          <div className="text-center">
            <p className="text-base font-semibold text-fg">
              Drag &amp; drop your CSV file here
            </p>
            <p className="text-sm text-fg-secondary mt-1">
              CSV files only · Up to {MAX_IMPORT_ROWS.toLocaleString()} rows
            </p>
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              inputRef.current?.click()
            }}
            className="px-4 py-2 rounded-lg border border-line bg-surface text-sm font-medium text-fg hover:bg-surface-sunken transition-colors shadow-sm"
          >
            Browse File
          </button>
        </div>

        {/* Inline parse / validation error */}
        {parseError && <div className="text-sm text-red-600">{parseError}</div>}

        {/* Uploaded file confirmation card */}
        {uploadedFile != null && importData != null && (
          <div className="bg-surface border border-line rounded-xl p-4 flex items-center gap-4">
            <div className="size-10 rounded-lg bg-success-bg flex items-center justify-center shrink-0">
              <FileText className="size-5 text-brand-bright" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-fg truncate">
                {uploadedFile.name}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-fg-secondary">
                  {formatFileSize(uploadedFile.size)}
                </span>
                <span className="size-1 rounded-full bg-line" />
                <div className="flex items-center gap-1">
                  <CheckCircle2 className="size-3.5 text-success" />
                  <span className="text-xs font-semibold text-success">
                    {importData.rows.length.toLocaleString()} contacts found
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              aria-label="Remove file"
              className="p-1.5 rounded-lg hover:bg-surface-sunken transition-colors"
            >
              <X className="size-4 text-fg-muted" />
            </button>
          </div>
        )}

        {/* Helper links */}
        <div className="flex items-center justify-end pt-1">
          <button
            type="button"
            onClick={downloadContactImportTemplate}
            className="flex items-center gap-1.5 text-sm text-fg-secondary hover:text-fg font-medium transition-colors"
          >
            <Download className="size-4" />
            Download CSV template
          </button>
        </div>
      </div>

      {/* Continue footer */}
      <div className="px-8 py-4 border-t border-line bg-surface flex justify-end">
        <button
          type="button"
          disabled={!hasValidFile}
          onClick={() => dispatch({ type: 'SET_STEP', step: 'map' })}
          className="bg-brand-bright hover:bg-brand-bright/90 text-white px-6 h-10 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Continue to Column Mapping →
        </button>
      </div>
    </div>
  )
}
